'use client';
import React, { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { QuizQuestionGrade } from '@/app/_lib/quiz';
import { ZocPreview } from '@/app/_lib/zoc';
import { secondsToHumanReadableTime } from '@/app/_lib/utils';
import mixpanel from 'mixpanel-browser';

type TrackerInitProps = {
  api_key: string;
  api_host: string;
};

export const TrackerClient = {
  realtimeRecordingStarted: (zocId: string) => {
    mixpanel.track('qc.realtime_recording.started', { zoc_id: zocId });
  },
  realtimeRecordingPaused: (zocId: string, secondsDuration: number) => {
    const recordingLength = secondsToHumanReadableTime(secondsDuration);
    mixpanel.track('qc.realtime_recording.paused', {
      zoc_id: zocId,
      recording_length: recordingLength,
      recording_length_seconds: secondsDuration,
    });
  },
  realtimeRecordingResumed: (zocId: string, secondsDuration: number) => {
    const recordingLength = secondsToHumanReadableTime(secondsDuration);
    mixpanel.track('qc.realtime_recording.resumed', {
      zoc_id: zocId,
      recording_length: recordingLength,
      recording_length_seconds: secondsDuration,
    });
  },
  realtimeRecordingStopped: (zocId: string, secondsDuration: number) => {
    const recordingLength = secondsToHumanReadableTime(secondsDuration);
    mixpanel.track('qc.realtime_recording.stopped', {
      zoc_id: zocId,
      recording_length: recordingLength,
      recording_length_seconds: secondsDuration,
    });
  },
  realtimeRecordingDiscarded: (zocId: string, secondsDuration: number) => {
    const recordingLength = secondsToHumanReadableTime(secondsDuration);
    mixpanel.track('qc.realtime_recording.discarded', {
      zoc_id: zocId,
      recording_length: recordingLength,
      recording_length_seconds: secondsDuration,
    });
  },
  questionCompleted: (quizAnswer: QuizQuestionGrade) => {
    mixpanel.track('qc.quiz.question_completed', {
      zoc_id: quizAnswer.zoc_id,
      question_level: quizAnswer.question_level,
      question_type: quizAnswer.question_type,
      question_number: quizAnswer.question_number,
      question_points_awarded: quizAnswer.question_points_awarded,
      question_points_possible: quizAnswer.question_points_possible,
      quiz_difficulty_level: quizAnswer.quiz_summary.quiz_level,
      quiz_language_code: quizAnswer.quiz_summary.quiz_language_code,
    });
  },
  pdfDownloaded: (zocId: string | undefined, languageCode: string) => {
    mixpanel.track('qc.zoc.pdf_downloaded', {
      zoc_id: zocId,
      language_code: languageCode,
    });
  },
  exportedToGoogle: (zocId: string | undefined) => {
    mixpanel.track('qc.zoc.exported_google', {
      zoc_id: zocId,
    });
  },
  shareLinkCopied: (zocId: string | undefined) => {
    mixpanel.track('qc.share.link_copied', { zoc_id: zocId });
  },
  sharePreviewViewed: (zocPreview: ZocPreview) => {
    mixpanel.track('qc.share.preview_viewed', {
      sharing_user_sub: zocPreview.sharing_user_sub,
      zoc_id: zocPreview.zoc_id,
    });
  },
  onboardingPreferredLanguageSelect: (language_code: string | undefined) => {
    const event = language_code ? 'language_selected' : 'skipped';
    mixpanel.track(`qc.translations.onboarding_${event}`, {
      ...(language_code && { language_code }),
    });
  },
  userPreferredLanguageChanged: (language_code: string) => {
    mixpanel.track('qc.translations.user_preferred_language_changed', {
      language_code,
    });
  },
  zocTranslationRequest: (zoc_id: string, language_code: string) => {
    mixpanel.track('qc.translations.zoc_translation_request', {
      zoc_id,
      language_code,
    });
  },
};

export default function TrackerInit({ api_key, api_host }: TrackerInitProps) {
  const pathname = usePathname();

  mixpanel.init(api_key, {
    api_host: api_host,
    api_routes: {
      track: 'trigger-event/',
      engage: 'engage/',
      groups: 'groups/',
    },
  });

  useEffect(() => {
    mixpanel.track_pageview();
  }, [pathname]);

  return <></>;
}
